import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import api from '../../services/api';
import paymentService from '../../services/paymentService';
import fonepayQR from '../../assets/fonepay-qr.jpeg';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft,
  User,
  MapPin,
  FileText,
  CreditCard,
  Truck,
  QrCode,
  CheckCircle2,
  ShieldCheck,
  UploadCloud,
  X,
  Loader2,
  Lock,
  Plus
} from 'lucide-react';

// Normalize a stored user phone (e.g. "98XXXXXXXX" or "+977-98XXXXXXXX") into
// the "+977XXXXXXXXXX" format this form edits and validates
const normalizePhone = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/[^\d]/g, '');
  const withCountryCode = digits.startsWith('977') ? digits : `977${digits}`;
  return `+${withCountryCode.substring(0, 13)}`;
};

const PAYMENT_METHODS = {
  PAY_LATER: 'pay-later',
  PAY_NOW: 'pay-now'
};

const ShippingDetailsStep = ({ onBack, onComplete, initialData, orderSummary, isSubmitting = false }) => {
  const { currentUser } = useAuth();
  const { formatPrice } = useCart();
  const [formData, setFormData] = useState({
    fullName: initialData?.fullName || currentUser?.name || '',
    phone: initialData?.phone || normalizePhone(currentUser?.phone),
    email: initialData?.email || currentUser?.email || '',
    address: initialData?.address || '',
    city: initialData?.city || '',
    panNumber: initialData?.panNumber || '',
    clinicName: initialData?.clinicName || '',
    orderNote: initialData?.orderNote || ''
  });

  const [showAdditionalDetails, setShowAdditionalDetails] = useState(
    Boolean(initialData?.panNumber || initialData?.clinicName || initialData?.orderNote)
  );
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.PAY_LATER);
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState('');
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef(null);

  // Prefill delivery address/city for a returning customer: their saved
  // profile first, falling back to their most recent order's shipping
  // address if the profile itself doesn't have one yet. Stays editable.
  useEffect(() => {
    if (!currentUser || initialData?.address) return;

    if (currentUser.address) {
      setFormData(prev => (prev.address ? prev : { ...prev, address: currentUser.address, city: prev.city || currentUser.city || '' }));
    }

    if (!currentUser.address) {
      api.get('/orders')
        .then((response) => {
          const lastOrder = response.data?.data?.orders?.[0];
          const shipping = lastOrder?.shippingDetails;
          if (!shipping) return;
          setFormData(prev => ({
            ...prev,
            address: prev.address || shipping.address || '',
            city: prev.city || shipping.city || ''
          }));
        })
        .catch(() => {
          // No prior orders or request failed - leave fields blank/editable
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle phone number formatting
  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/[^\d]/g, ''); // Keep only digits

    // Add the +977 prefix if not already there
    if (!value.startsWith('977')) {
      value = '977' + value;
    }

    // Limit to +977 followed by 10 digits
    if (value.length > 13) {
      value = value.substring(0, 13);
    }

    // Format with the '+' sign
    const formattedValue = '+' + value;

    setFormData(prev => ({
      ...prev,
      phone: formattedValue
    }));
  };

  const handlePaymentMethodChange = useCallback((method) => {
    setPaymentMethod(method);

    // Clear file-related state when switching away from pay-now
    if (method !== PAYMENT_METHODS.PAY_NOW) {
      setScreenshotFile(null);
      setScreenshotPreview('');
      setFileError('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, []);

  const validateFile = useCallback((file) => {
    const validation = paymentService.validatePaymentFile(file);
    return validation.errors.length > 0 ? validation.errors[0] : null;
  }, []);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files[0];
    setFileError('');

    if (!file) {
      setScreenshotFile(null);
      setScreenshotPreview('');
      return;
    }

    const validationError = validateFile(file);
    if (validationError) {
      setFileError(validationError);
      setScreenshotFile(null);
      setScreenshotPreview('');
      toast.error(validationError);
      return;
    }

    setScreenshotFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setScreenshotPreview(reader.result);
    };
    reader.onerror = () => {
      setFileError('Failed to read file');
      toast.error('Failed to read file');
    };
    reader.readAsDataURL(file);
  }, [validateFile]);

  const handleFileRemove = useCallback(() => {
    setScreenshotFile(null);
    setScreenshotPreview('');
    setFileError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const paymentFormErrors = useMemo(() => {
    if (paymentMethod === PAYMENT_METHODS.PAY_NOW && screenshotFile) {
      const fileValidation = validateFile(screenshotFile);
      if (fileValidation) return { screenshot: fileValidation };
    }
    return {};
  }, [paymentMethod, screenshotFile, validateFile]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required shipping fields
    const requiredFields = ['fullName', 'phone', 'email', 'address', 'city'];
    const missingFields = requiredFields.filter(field => !formData[field].trim());

    if (missingFields.length > 0) {
      toast.error(`Please fill in the following required fields: ${missingFields.join(', ')}`);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!formData.phone.match(/^\+977\d{10}$/)) {
      toast.error('Please enter a valid phone number in format +977XXXXXXXXXX');
      return;
    }

    if (paymentMethod === PAYMENT_METHODS.PAY_NOW && !screenshotFile) {
      toast.error('Please upload a payment screenshot for online payment');
      return;
    }

    if (Object.keys(paymentFormErrors).length > 0) {
      toast.error('Please fix the payment errors before submitting');
      return;
    }

    onComplete({
      ...formData,
      paymentMethod,
      screenshotFile: paymentMethod === PAYMENT_METHODS.PAY_NOW ? screenshotFile : null
    });
  };

  const inputClasses = 'w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-secondary-500 focus:border-transparent transition-colors disabled:bg-gray-50 disabled:text-gray-400';
  const labelClasses = 'block text-sm font-medium text-gray-700 mb-1.5';

  const SectionCard = (props) => {
    const SectionIcon = props.icon;
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
        <div className="flex items-center gap-2.5 mb-5">
          <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-secondary-50 text-secondary-600">
            <SectionIcon className="w-4 h-4" strokeWidth={2} />
          </span>
          <h3 className="text-base font-semibold text-gray-900">{props.title}</h3>
          {props.tag && <span className="text-xs font-normal text-gray-400">{props.tag}</span>}
        </div>
        {props.children}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-start">
      {/* Main column */}
      <div className="lg:col-span-2 space-y-5">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800 disabled:opacity-50 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Cart
        </button>

        {/* Contact Information */}
        <SectionCard icon={User} title="Contact Information">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="fullName" className={labelClasses}>Full Name*</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className={inputClasses}
              />
            </div>

            <div>
              <label htmlFor="phone" className={labelClasses}>Phone Number*</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                required
                disabled={isSubmitting}
                className={inputClasses}
              />
              <p className="text-xs text-gray-400 mt-1">Format: +977 followed by 10 digits</p>
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="email" className={labelClasses}>Email*</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className={inputClasses}
              />
            </div>
          </div>
        </SectionCard>

        {/* Delivery Address */}
        <SectionCard icon={MapPin} title="Delivery Address">
          <div className="space-y-4">
            <div>
              <label htmlFor="address" className={labelClasses}>Street Address*</label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                rows="3"
                disabled={isSubmitting}
                className={`${inputClasses} resize-none`}
              ></textarea>
            </div>

            <div>
              <label htmlFor="city" className={labelClasses}>City*</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className={inputClasses}
              />
            </div>
          </div>
        </SectionCard>

        {/* Additional Details - collapsed by default since all 3 fields are
            optional and most orders don't need them; expands on demand. */}
        {showAdditionalDetails ? (
          <SectionCard icon={FileText} title="Additional Details" tag="Optional">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="panNumber" className={labelClasses}>PAN Number</label>
                <input
                  type="text"
                  id="panNumber"
                  name="panNumber"
                  value={formData.panNumber}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={inputClasses}
                />
              </div>

              <div>
                <label htmlFor="clinicName" className={labelClasses}>Clinic/Hospital Name</label>
                <input
                  type="text"
                  id="clinicName"
                  name="clinicName"
                  value={formData.clinicName}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={inputClasses}
                />
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="orderNote" className={labelClasses}>Order Note</label>
                <textarea
                  id="orderNote"
                  name="orderNote"
                  value={formData.orderNote}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Any special instructions for your order"
                  disabled={isSubmitting}
                  className={`${inputClasses} resize-none`}
                ></textarea>
              </div>
            </div>
          </SectionCard>
        ) : (
          <button
            type="button"
            onClick={() => setShowAdditionalDetails(true)}
            className="flex items-center gap-1.5 text-sm font-medium text-secondary-600 hover:text-secondary-700 transition-colors"
          >
            <Plus className="w-4 h-4" strokeWidth={2} />
            Add PAN number, clinic name, or an order note
            <span className="text-gray-400 font-normal">(optional)</span>
          </button>
        )}

        {/* Payment Method */}
        <SectionCard icon={CreditCard} title="Payment Method">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Cash on Delivery */}
            <label
              className={`relative flex items-start gap-3 rounded-xl border-2 p-4 cursor-pointer transition-all ${paymentMethod === PAYMENT_METHODS.PAY_LATER
                  ? 'border-secondary-500 bg-secondary-50/60'
                  : 'border-gray-200 hover:border-gray-300'
                } ${isSubmitting ? 'pointer-events-none opacity-60' : ''}`}
            >
              <input
                type="radio"
                name="paymentMethod"
                value={PAYMENT_METHODS.PAY_LATER}
                checked={paymentMethod === PAYMENT_METHODS.PAY_LATER}
                onChange={(e) => handlePaymentMethodChange(e.target.value)}
                disabled={isSubmitting}
                className="sr-only"
              />
              <span className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg ${paymentMethod === PAYMENT_METHODS.PAY_LATER ? 'bg-secondary-600 text-white' : 'bg-gray-100 text-gray-500'
                }`}>
                <Truck className="w-5 h-5" strokeWidth={2} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">Cash on Delivery</p>
                <p className="text-xs text-gray-500 mt-0.5">Pay when your order arrives</p>
              </div>
              {paymentMethod === PAYMENT_METHODS.PAY_LATER && (
                <CheckCircle2 className="w-5 h-5 text-secondary-600 flex-shrink-0" strokeWidth={2} />
              )}
            </label>

            {/* Online Payment (disabled) */}
            <label className="relative flex items-start gap-3 rounded-xl border-2 border-gray-200 bg-gray-50 p-4 cursor-not-allowed">
              <input type="radio" name="paymentMethod" value={PAYMENT_METHODS.PAY_NOW} disabled className="sr-only" />
              <span className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg bg-gray-200 text-gray-400">
                <QrCode className="w-5 h-5" strokeWidth={2} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-gray-500">Online Payment</p>
                  <span className="text-[10px] font-bold uppercase tracking-wide bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded-full">
                    Coming Soon
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">Pay instantly via Fonepay QR</p>
              </div>
            </label>
          </div>

          {/* Payment Options for Pay Now */}
          {paymentMethod === PAYMENT_METHODS.PAY_NOW && (
            <div className="mt-5 bg-gray-50 rounded-xl border border-gray-100 p-4 space-y-4">
              <h4 className="text-sm font-semibold text-gray-900">Scan QR and Pay</h4>

              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex flex-col items-center space-y-3">
                  <img
                    src={fonepayQR}
                    alt="Fonepay QR Code"
                    className="w-48 h-48 sm:w-64 sm:h-64 max-w-full object-contain border border-gray-200 rounded-md"
                    onError={(e) => {
                      e.target.src = '/images/placeholder-qr.png';
                      e.target.onerror = null;
                    }}
                  />
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-1">Scan this QR code to pay via Fonepay</p>
                    <p className="text-xs text-gray-500">Amount: Rs. {formatPrice(orderSummary?.total || 0)}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Upload Payment Screenshot *
                </label>

                <div
                  onClick={() => !isSubmitting && fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer transition-colors duration-200 bg-white ${isSubmitting
                      ? 'cursor-not-allowed opacity-50'
                      : screenshotFile
                        ? 'border-secondary-300 bg-secondary-50'
                        : fileError
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                    }`}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    className="hidden"
                    disabled={isSubmitting}
                  />

                  {screenshotPreview ? (
                    <div className="space-y-3 w-full">
                      <div className="relative">
                        <img
                          src={screenshotPreview}
                          alt="Payment Screenshot Preview"
                          className="max-h-48 mx-auto object-contain rounded-md"
                        />
                        {!isSubmitting && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFileRemove();
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                            aria-label="Remove image"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500">{screenshotFile?.name}</p>
                        <p className="text-xs text-gray-400">
                          {screenshotFile && `${(screenshotFile.size / 1024 / 1024).toFixed(2)} MB`}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 text-center">
                      <UploadCloud className={`h-8 w-8 mx-auto ${fileError ? 'text-red-400' : 'text-gray-400'}`} strokeWidth={1.5} />
                      <p className="text-sm text-gray-500">Click to upload payment screenshot</p>
                      <p className="text-xs text-gray-400">PNG, JPG, JPEG up to 5MB</p>
                    </div>
                  )}
                </div>

                {(fileError || paymentFormErrors.screenshot) && (
                  <p className="text-sm text-red-500 mt-1">{fileError || paymentFormErrors.screenshot}</p>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-700 font-medium mb-1">Instructions</p>
                  <ul className="text-xs text-blue-600 space-y-1">
                    <li>1. Scan the QR code with your mobile banking app</li>
                    <li>2. Complete the payment transaction</li>
                    <li>3. Take a screenshot of the successful payment confirmation</li>
                    <li>4. Upload the screenshot here</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </SectionCard>
      </div>

      {/* Sidebar - Order Summary & Submit */}
      <div className="lg:col-span-1">
        <div className="lg:sticky lg:top-8 space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Order Summary</h3>

            {orderSummary && (
              <div className="space-y-2.5">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Subtotal</span>
                  <span>Rs. {formatPrice(orderSummary.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Shipping</span>
                  <span>Rs. {formatPrice(orderSummary.shipping)}</span>
                </div>
                <div className="flex justify-between text-base font-semibold text-gray-900 pt-2.5 border-t border-gray-100">
                  <span>Total</span>
                  <span>Rs. {formatPrice(orderSummary.total)}</span>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className={`mt-5 w-full py-3 px-4 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${isSubmitting
                  ? 'bg-gray-200 cursor-not-allowed text-gray-500'
                  : 'bg-secondary-600 hover:bg-secondary-700 text-white shadow-sm'
                }`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Place Order
                  <Lock className="w-4 h-4" strokeWidth={2} />
                </>
              )}
            </button>

            <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-gray-400">
              <ShieldCheck className="w-3.5 h-3.5" />
              Secure checkout - we never store your payment details
            </p>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ShippingDetailsStep;
